import { readPreference, writePreference } from './config.ts';
import { DEFAULT_NOTIFICATION_SETTINGS, type NotificationSettings } from './notifications.ts';

// ------------------------------------------------------------------------------ this device's marks

const READ = 'notifications.read';
const HIDDEN = 'notifications.hidden';
const SHOWN = 'notifications.shown';
const SETTINGS = 'notifications.settings';
/** Marks of notifications long gone are dropped; enough for months of them. */
const KEEP_MARKS = 400;

function readSet(name: string): Set<string> {
  try {
    const value = JSON.parse(readPreference(name) ?? '[]') as unknown;
    return new Set(Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeSet(name: string, keys: Iterable<string>): void {
  writePreference(name, JSON.stringify([...keys].slice(-KEEP_MARKS)));
}

export const notificationMarks = {
  read: () => readSet(READ),
  hidden: () => readSet(HIDDEN),
  /** Already shown as a system notification (each only once). */
  shown: () => readSet(SHOWN),
  markRead(keys: Iterable<string>) {
    const set = readSet(READ);
    for (const key of keys) set.add(key);
    writeSet(READ, set);
  },
  hide(key: string) {
    const set = readSet(HIDDEN);
    set.add(key);
    writeSet(HIDDEN, set);
    this.markRead([key]);
  },
  markShown(keys: Iterable<string>) {
    const set = readSet(SHOWN);
    for (const key of keys) set.add(key);
    writeSet(SHOWN, set);
  },
};

export function readNotificationSettings(): NotificationSettings {
  try {
    const stored = JSON.parse(readPreference(SETTINGS) ?? '{}') as Partial<Record<keyof NotificationSettings, unknown>>;
    const flag = (name: keyof NotificationSettings) => (typeof stored[name] === 'boolean' ? (stored[name] as boolean) : (DEFAULT_NOTIFICATION_SETTINGS[name] as boolean));
    const from = typeof stored.largeFromMinor === 'string' && /^\d{1,12}$/.test(stored.largeFromMinor) ? BigInt(stored.largeFromMinor) : DEFAULT_NOTIFICATION_SETTINGS.largeFromMinor;
    return { budgets: flag('budgets'), payments: flag('payments'), large: flag('large'), largeFromMinor: from, settlement: flag('settlement'), banks: flag('banks'), system: flag('system') };
  } catch {
    return DEFAULT_NOTIFICATION_SETTINGS;
  }
}

export function writeNotificationSettings(settings: NotificationSettings): void {
  writePreference(SETTINGS, JSON.stringify({ ...settings, largeFromMinor: String(settings.largeFromMinor) }));
}
